import { useEffect, useRef, useState } from "react";
import { useQuery } from "seed/gql";

// Owns the message list of a chat: the query, the merge of freshly fetched
// messages into local state, and the id cursor that keeps the query incremental.
//
// Messages arrive from two places — this poll/refetch, and optimistic bubbles the
// component pushes while a request is in flight — so merging is the whole job here.

const MESSAGES_QUERY = `{
  chatMessages {
    createdAt
    content
    action
    settings
    type
    isTemplate
    chat {
      id
    }
    templateApplied {
      templateName
    }
  }
}`;

const POLL_INTERVAL = 15 * 1000;
// Collapses the burst of websocket events an execution fires (one per step) into
// a single query.
const REFETCH_DEBOUNCE = 150;
// A message this recent is the one that just arrived, so it is what to scroll to.
const RECENT_MESSAGE_WINDOW = 2000;

// Escaping differs between the optimistic bubble and the stored message, so plain
// text is compared unescaped.
const toComparableText = (text = "") => text.replace(/\\(.)/g, "$1").trim();

const isVisible = (message) =>
  (message.settings?.visible ?? true) ||
  message.settings?.finish_execution ||
  message.settings?.is_initial_message;

const useChatMessages = (chatId, { onBotMessagesArrived, onMessagesAppended }) => {

  const [messages, setMessages] = useState([]);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const refetchTimeoutRef = useRef(null);

  // Once the first page has loaded, only ask for messages at or past the cursor.
  const idFilter = (!isInitialized && messages.length == 0) || !lastMessageId
    ? ""
    : `AND id >= ${lastMessageId}`;

  const reqMessages = useQuery(MESSAGES_QUERY, `(chat_id=${chatId} ${idFilter})`, {
    fetchPolicy: "no-cache",
    cacheQuery: false,
    partialRefetch: false,
    pollInterval: POLL_INTERVAL,
    orderBy: "created_at",
    onCompleted: (data) => {

      // A real bot reply (not just the "thinking" placeholder) means the turn is over.
      const arrivedBotMessages = data.chatMessages
        .filter(message => !messages.find(current => message.id == current.id))
        .filter(message => !message.isTemplate)
        .filter(isVisible)
        .filter(message => message.type == "BOT_MESSAGE");
      const hasLoadingMessage = arrivedBotMessages
        .some(message => message.settings?.is_loading_message);
      if (arrivedBotMessages.length > 0 && !hasLoadingMessage)
        onBotMessagesArrived();

      // Marked before the empty check: a chat with no messages is loaded all the same,
      // and the conversation reads this to know its first fetch has come back.
      setIsInitialized(true);

      if (data.chatMessages.length == 0) return;

      // Refetched messages carry updated settings, so the fetched copy always replaces
      // the local one.
      const previousCount = messages.length;
      const fetchedIds = data.chatMessages.map(message => message.id);
      const fetchedTexts = new Set(
        data.chatMessages.map(message => toComparableText(message.content?.plain))
      );

      const mergedMessages = [
        ...messages.filter(message => !fetchedIds.includes(message.id)),
        ...data.chatMessages,
      ]
        .filter(message => message.id)
        // Drop the optimistic bubble once its stored counterpart comes back.
        .filter(message => !message.isUITemp || !fetchedTexts.has(toComparableText(message.content?.plain)))
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      setMessages(mergedMessages);

      // Optimistic temp ids are ignored so they don't push the cursor forward.
      const maxId = mergedMessages.reduce(
        (max, message) => (!message.isUITemp && Number(message.id) > max ? Number(message.id) : max), 0);
      if (maxId > 0) setLastMessageId(maxId);

      if (previousCount < mergedMessages.length) {
        const arrivedMessage = mergedMessages.find(message =>
          new Date(message.createdAt) > new Date() - RECENT_MESSAGE_WINDOW &&
          message.settings?.visible == true);
        onMessagesAppended(arrivedMessage?.id);
      }
    },
  });

  const refetchMessages = () => reqMessages.refetch();

  const refetchMessagesDebounced = () => {
    if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
    refetchTimeoutRef.current = setTimeout(() => reqMessages.refetch(), REFETCH_DEBOUNCE);
  };

  useEffect(() => {
    reqMessages.refetch();
    // The first messages of a brand new chat are written server-side just after it
    // is created, so a single immediate fetch can come back empty.
    const retryTimeout = setTimeout(() => reqMessages.refetch(), 1000);
    return () => clearTimeout(retryTimeout);
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
  }, []);

  return {
    messages,
    setMessages,
    isInitialized,
    refetchMessages,
    refetchMessagesDebounced,
  };
};

export default useChatMessages;
